import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import UserSelect, { IUserOption } from "./UserSelect";
import { MenuPlacement } from "react-select";
import userSelectors from "redux/app/user/selectors";
import userActions from "redux/app/user/actions";
import { IListObjectStore } from "redux/_common/type";
import { IUser } from "redux/app/user/@types";
import vdmDashboardActions from "redux/app/vdmdashboard/actions";
import {
	IVDMPICPerson,
	TAssignView,
	TDepartmentID,
	TDepartmentWiseAssignment,
} from "redux/app/vdmdashboard/@types";
import PhoneField from "components/_common/PhoneField";
import { vdmDashboardUtils } from "redux/app/vdmdashboard/utils";

const { isPerson, isSlotPersisted } = vdmDashboardUtils;

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png"];

interface IDepartmentPosition {
	id: "manager" | "superintendent" | "executive";
	title: string;
	unique?: boolean;
	menuPlacement?: MenuPlacement;
}

interface IDepartmentConfig {
	id: TDepartmentID;
	title: string;
	isDisabled?: boolean;
	positions: IDepartmentPosition[];
}

const departmentConfig: IDepartmentConfig[] = [
	{
		id: "technical",
		title: "Technical",
		positions: [
			{ id: "manager", title: "Manager" },
			{ id: "superintendent", title: "Superintendent" },
			{ id: "executive", title: "Executive", menuPlacement: "top" },
		],
	},
	{
		id: "operations_commercial",
		title: "Operations/Commercial",
		positions: [
			{ id: "manager", title: "Manager", unique: true },
			{ id: "superintendent", title: "Superintendent" },
			{ id: "executive", title: "Executive" },
		],
	},
	{
		id: "marine_hr",
		title: "Marine HR",
		positions: [
			{ id: "manager", title: "Manager", unique: true },
			{ id: "executive", title: "Executive" },
		],
	},
	{
		id: "sqv_inspections",
		title: "SQV/Inspections",
		positions: [
			{ id: "manager", title: "Manager", unique: true },
			{ id: "superintendent", title: "Superintendent" },
			{ id: "executive", title: "Executive" },
		],
	},
];

type OwnProps = {
	isAdmin?: boolean;
	vesselId: string;
	isLoading: boolean;
	isBeingSaved?: boolean;
	isBeingUploaded?: boolean;
	activeDepartment: TDepartmentID;
	setActiveDepartment: React.Dispatch<React.SetStateAction<TDepartmentID>>;
	setActiveView: React.Dispatch<React.SetStateAction<TAssignView>>;
	localAssignment: TDepartmentWiseAssignment;
	setLocalAssignment: React.Dispatch<
		React.SetStateAction<TDepartmentWiseAssignment>
	>;
	baseline: TDepartmentWiseAssignment;
	isDirty: boolean;
	onPictureSelected?: (personId: string, previewUrl: string) => void;
	localPicturePreviews?: Record<string, string>;
};

type AssignUsersProps = PropsFromRedux & OwnProps;

function AssignUsers(props: AssignUsersProps) {
	const {
		allUsers,
		vesselId,
		savePICDepartmentWiseAssignment,
		isLoading,
		isBeingSaved,
		isBeingUploaded,
		activeDepartment,
		setActiveDepartment,
		setActiveView,
		localAssignment,
		setLocalAssignment,
		baseline,
		isDirty,
		onPictureSelected,
		localPicturePreviews,
	} = props;

	useEffect(() => {
		props.loadUsers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [uploadError, setUploadError] = useState<string | null>(null);

	const usersList = useMemo(() => {
		return allUsers?.ids
			? {
					...allUsers,
					ids: allUsers.ids.filter(
						(id) => allUsers.byIds[id]?.role === "tenantAdmin",
					),
				}
			: allUsers;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allUsers]);

	const transformUsers = (
		usersList: IListObjectStore<IUser>,
	): IUserOption[] => {
		return (
			usersList?.ids?.map((id) => {
				const user = usersList?.byIds?.[id];
				return {
					value: user.id,
					isRelieved: user?.isRelieved,
					name: user.name,
					staffId: user.staffId,
					email: user.email,
				};
			}) || []
		);
	};

	const users = useMemo(() => transformUsers(usersList), [usersList]);

	// Live lookup - phone_number/profile_picture  lives in the user, Used to decide what step 2 shows.
	const getLiveUser = (personId: string | null) =>
		personId ? allUsers?.byIds?.[personId] : undefined;

	const handleUserChange = (
		departmentId: TDepartmentID,
		positionId: IDepartmentPosition["id"],
		opt: IUserOption | null,
	) => {
		setLocalAssignment((prev) => {
			const current = prev[departmentId]?.[positionId];
			const isSamePerson = isPerson(current) && opt?.value === current.id;

			const person: IVDMPICPerson | {} = opt
				? {
						id: opt.value,
						name: opt.name,
						email: opt.email || "",
						// Preserve manually-entered phone if re-picking the same
						// person; otherwise prefill from their user record if
						// known (still editable), else blank.
						phone_number: isSamePerson
							? current.phone_number
							: getLiveUser(opt.value)?.phone_number || "",
						// managed thru step 2.
						profile_picture: "",
					}
				: {};

			return {
				...prev,
				[departmentId]: {
					...prev[departmentId],
					[positionId]: person,
				},
			};
		});
	};

	const handlePhoneNumberChange = (
		departmentId: TDepartmentID,
		positionId: IDepartmentPosition["id"],
		phoneNumber: string,
	) => {
		setLocalAssignment((prev) => {
			const current = prev[departmentId]?.[positionId];
			if (!isPerson(current)) return prev;

			return {
				...prev,
				[departmentId]: {
					...prev[departmentId],
					[positionId]: { ...current, phone_number: phoneNumber },
				},
			};
		});
	};

	const handleProfilePictureUpload = async (file: File, personId: string) => {
		if (!ALLOWED_MIME_TYPES.includes(file.type)) {
			setUploadError("Only JPG or PNG images are allowed.");
			return;
		}

		if (file.size > MAX_FILE_SIZE_BYTES) {
			setUploadError("File size must not exceed 5MB.");
			return;
		}

		if (!vesselId || !personId) {
			setUploadError(
				"Missing vessel or user information. Please refresh and try again.",
			);
			return;
		}

		// Show the picture instantly from the file itself - don't wait for backend to serve
		const previewUrl = URL.createObjectURL(file);
		onPictureSelected?.(personId, previewUrl);

		props.uploadPICProfilePicture(vesselId, personId, file);
	};

	const handleSave = async () => {
		savePICDepartmentWiseAssignment(vesselId, localAssignment);
	};

	const handleCancel = () => {
		setLocalAssignment(baseline);
	};

	const isLocked = isLoading;

	const renderDepartmentWisePositions = (departmentId: TDepartmentID) => {
		const tabIndex = departmentConfig.findIndex((dc) => dc.id === departmentId);
		const positions = departmentConfig[tabIndex]?.positions;

		return (
			<div className="mt-4 flex flex-col gap-3">
				{positions?.map((position) => {
					const { id: positionId, title, menuPlacement = undefined } = position;

					const assignedPerson = localAssignment?.[departmentId]?.[positionId];
					const assignedUserId = isPerson(assignedPerson)
						? assignedPerson.id
						: null;
					const phoneNumber = isPerson(assignedPerson)
						? assignedPerson.phone_number
						: "";

					const liveUser = getLiveUser(assignedUserId);
					const hasPicture =
						!!(assignedUserId && localPicturePreviews?.[assignedUserId]) ||
						!!liveUser?.profile_picture?.attachmentName;
					const slotSaved = isSlotPersisted(
						baseline,
						departmentId,
						positionId,
						assignedPerson,
					);

					return (
						<div
							className="rounded-lg border border-slate-200 p-4"
							key={positionId}
						>
							<h3 className="font-medium mb-3">{title}</h3>

							{/* Step 1: assign user + phone number - draft, gated by Save/Cancel */}
							<div className="flex flex-col md:flex-row md:items-start space-y-4 md:space-y-0 md:space-x-8">
								<UserSelect
									options={users}
									value={
										assignedUserId
											? users.find((u) => u.value === assignedUserId) || null
											: null
									}
									menuPlacement={menuPlacement}
									onChange={(opt) =>
										handleUserChange(departmentId, positionId, opt)
									}
									isDisabled={isLocked}
								/>

								<PhoneField
									value={phoneNumber}
									onChange={(value) =>
										handlePhoneNumberChange(departmentId, positionId, value)
									}
									disabled={!assignedUserId || isLocked}
									defaultCountry="SG"
									inputClassName={"py-2"}
								/>
							</div>

							{uploadError && (
								<p className="mt-1 text-xs text-red-600 w-full basis-full">
									{uploadError}
								</p>
							)}

							{/* Step 2: picture - only once the assignment for this slot is persisted */}
							{assignedUserId ? (
								slotSaved ? (
									<div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex items-center gap-3">
										<span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
											Profile picture
										</span>

										<input
											type="file"
											accept="image/jpeg,image/jpg,image/png"
											disabled={isLocked}
											className="hidden"
											id={`pic-upload-${departmentId}-${positionId}`}
											onChange={(e) => {
												const file = e.target.files?.[0];
												if (file)
													handleProfilePictureUpload(file, assignedUserId);
												e.target.value = "";
											}}
										/>

										{hasPicture && (
											<button
												type="button"
												disabled={isLocked || isBeingUploaded}
												className={classNames(
													"text-sm font-medium cursor-pointer",
													isLocked
														? "text-slate-300 cursor-not-allowed"
														: "text-slate-600 hover:text-slate-800",
													{
														"cursor-not-allowed": isLoading,
													},
												)}
												onClick={() => {
													if (isLoading) return;
													setActiveView("view");
												}}
											>
												<span className="bp3-icon bp3-icon-eye-open text-lg mr-2 cursor-pointer" />
												<span className="hidden sm:inline-block">
													View picture
												</span>
											</button>
										)}

										<label
											htmlFor={`pic-upload-${departmentId}-${positionId}`}
											className={classNames(
												"text-sm font-medium cursor-pointer",
												isLocked
													? "text-slate-300 cursor-not-allowed pointer-events-none"
													: "text-blue-600 hover:text-blue-700",
											)}
										>
											<span className="bp3-icon bp3-icon-upload text-lg mr-2" />
											<span className="hidden sm:inline-block cursor-pointer">
												{isBeingUploaded
													? "Uploading..."
													: hasPicture
														? "Change picture"
														: "Upload picture"}
											</span>
										</label>
									</div>
								) : (
									<div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex items-center gap-2 text-xs text-amber-600">
										<span className="bp3-icon bp3-icon-warning-sign" />
										Save this assignment to enable picture upload
									</div>
								)
							) : (
								<p className="mt-2 text-xs text-slate-400">
									Assign a user to this position to add a profile picture.
								</p>
							)}
						</div>
					);
				})}
			</div>
		);
	};

	return (
		<div className="w-full mt-4 gap-x-4 pb-4 px-4 rounded mb-10">
			{props.isAdmin && (
				<div className="flex overflow-x-auto mb-4 border-b border-gray-200 gap-1">
					{departmentConfig.map((tab) => {
						const { id, title, isDisabled } = tab;
						return (
							<button
								key={id}
								className={classNames(
									"px-3 py-2 text-sm font-medium whitespace-nowrap transition-all duration-200",
									{
										"text-blue-600 bg-blue-50 border-b-2 border-blue-600":
											activeDepartment === id,
										"text-gray-700 hover:bg-gray-100 border-b-2 border-transparent":
											activeDepartment !== id && !isDisabled,
										"text-gray-500 cursor-not-allowed": isDisabled,
									},
								)}
								onClick={() => {
									if (isDisabled) return;
									setActiveDepartment(id);
								}}
								disabled={isDisabled}
							>
								{title}
							</button>
						);
					})}
				</div>
			)}

			<div>{renderDepartmentWisePositions(activeDepartment)}</div>

			<div className="flex items-center gap-3 mt-6 pt-4 border-t border-gray-200">
				<button
					className={classNames(
						"px-4 py-1.5 text-sm font-medium rounded-md transition-colors",
						isDirty && !isLocked
							? "bg-blue-600 text-white hover:bg-blue-700"
							: "bg-blue-200 text-white cursor-not-allowed",
					)}
					disabled={!isDirty || isLocked}
					onClick={handleSave}
				>
					{isBeingSaved ? "Saving..." : "Save"}
				</button>
				<button
					className={classNames(
						"px-4 py-1.5 text-sm font-medium rounded-md border transition-colors",
						isDirty && !isLocked
							? "border-gray-300 text-gray-700 hover:bg-gray-100"
							: "border-gray-200 text-gray-400 cursor-not-allowed",
					)}
					disabled={!isDirty || isLocked}
					onClick={handleCancel}
				>
					Cancel
				</button>
			</div>
		</div>
	);
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const userStore = userSelectors._getStore(store);
	const allUsers = userSelectors.getUsersList(userStore);
	return {
		allUsers,
	};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {
		loadUsers() {
			return dispatch(userActions.commands.userListLoad(1, false));
		},
		uploadPICProfilePicture(vesselId: string, userId: string, file: File) {
			return dispatch(
				vdmDashboardActions.commands.uploadPICProfilePicture(
					vesselId,
					userId,
					file,
				), // adjust to real action name
			);
		},
		savePICDepartmentWiseAssignment(
			vesselId: string,
			departmentWiseAssignment: TDepartmentWiseAssignment,
		) {
			return dispatch(
				vdmDashboardActions.commands.vdmPICSave(
					vesselId,
					departmentWiseAssignment,
				),
			);
		},
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(AssignUsers);
